import { AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";
import { ApiService } from "../../../config";
import { handleToken } from "../../../utils/expiredTokenLogout";
import { handleError } from "../../../utils/parseApiReturn";

interface GetProfilesParams {
  status?: string;
  ranking?: string;
  localidade?: string;
  query?: string;
  page?: number;
  size?: number;
  sort?: string;
  token: string;
}

const getCustomers = async (params: GetProfilesParams): Promise<any> => {
  try {
    const { token, ...queryParams } = params;
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: queryParams,
    };

    const result = await ApiService.get("/customers", config);
    const { data, error } = result.data;
    if (error) {
      handleToken(result.data.message);
      const { message } = handleError(error, "Erro ao obter Clientes");
      toast.error(message);
      return null;
    }
    return data;
  } catch (error) {
    const { message } = handleError(error, "Erro inesperado ao obter Clientes");
    toast.error(message);
    return null;
  }
};

const syncKeywords = async (params: {
  businessProfileId: string;
  client: 'radar' | 'serp_api';
  scope?: 'all' | 'gabarito';
  token: string;
}) => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${params.token}`,
      },
    };
    const result = await ApiService.post(
      params.client === "serp_api"
        ? `business-profiles/${params.businessProfileId}/keywords/ranking/sync-serp`
        : `business-profiles/${params.businessProfileId}/keywords/ranking/radar?scope=${params.scope ?? "all"}`,
      {},
      config
    );
    const syncResult = result.data.data;
    if (syncResult) {
      const processed = Number(syncResult.processed ?? 0);
      const retryScheduled = Number(syncResult.retryScheduled ?? 0);
      if (retryScheduled > 0) {
        toast.warn(
          `${processed} palavras atualizadas. ${retryScheduled} serão tentadas novamente no próximo ciclo.`,
        );
      } else {
        toast.success(`${processed} palavras sincronizadas com sucesso.`);
      }
    } else {
      toast.error("Erro ao sincronizar keywords.");
    }
  } catch (error) {
    const { message } = handleError(
      error,
      "Erro inesperado ao sincronizar keywords."
    );
    toast.error(message);
    return null;
  }
};

const switchAiCommentResponse = async (params: { businessProfileId: string, allowAiCommentResponse: boolean, token: string }) => {
  const { businessProfileId, allowAiCommentResponse, token } = params;
  if (!token) {
    toast.error("Token de autenticação não encontrado.");
    throw new Error("Token de autenticação não encontrado.");
  }

  const config: AxiosRequestConfig = {
    headers: { Authorization: `Bearer ${token}` },
  };

  let result;
  try {
    result = await ApiService.put(
      `business-profiles/${businessProfileId}/suspended-ai-review-replies`,
      { suspendedAiReviewReplies: allowAiCommentResponse },
      config
    );
  } catch (error) {
    const { message } = handleError(error, "Erro inesperado ao atualizar configuração de resposta automática de comentários.");
    toast.error(message);
    throw error;
  }

  if (result.data.error) {
    toast.error(result.data.message);
    throw new Error(result.data.message);
  }

  toast.success("Configuração de resposta automática de comentários atualizada com sucesso.");
}

export const CustomersService = { getCustomers, syncKeywords, switchAiCommentResponse };
